
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { 
  FileText, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Globe,
  Users,
  AlertTriangle
} from "lucide-react";

interface EmptyApplicationsStateProps {
  activeTab: string;
}

const getEmptyStateConfig = (tab: string) => {
  const configs = {
    pending: {
      icon: Clock,
      title: "No Pending Applications",
      description: "There are currently no applications waiting for initial review.",
      color: "text-blue-500",
      bgColor: "bg-blue-50"
    },
    approved: {
      icon: CheckCircle,
      title: "No Approved Applications",
      description: "There are currently no applications that have been approved but not yet published.",
      color: "text-green-500",
      bgColor: "bg-green-50"
    },
    rejected: {
      icon: XCircle,
      title: "No Rejected Applications",
      description: "There are currently no rejected applications. Rejected applications can be reopened for review if needed.",
      color: "text-red-500",
      bgColor: "bg-red-50"
    },
    published: {
      icon: Globe,
      title: "No Published Experts",
      description: "There are currently no experts published on the marketplace. This shows experts who are live and visible to clients on the consultations page.",
      color: "text-green-600",
      bgColor: "bg-green-50"
    }
  };

  return configs[tab as keyof typeof configs] || configs.pending;
};

export default function EmptyApplicationsState({ activeTab }: EmptyApplicationsStateProps) {
  const config = getEmptyStateConfig(activeTab);
  const IconComponent = config.icon;

  return (
    <Card>
      <CardContent className="py-12">
        <div className={`text-center space-y-4 ${config.bgColor} p-8 rounded-lg`}>
          <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-white ${config.color}`}>
            <IconComponent className="h-8 w-8" />
          </div>
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-gray-900">
              {config.title}
            </h3>
            <p className="text-sm text-gray-600 max-w-md mx-auto">
              {config.description}
            </p>
          </div>
          
          {activeTab === "rejected" && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-2 text-amber-800 justify-center">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-xs font-medium">
                  Rejected applications can be reopened through the workflow controls
                </span>
              </div>
            </div>
          )}
          
          {activeTab === "published" && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 text-blue-800 justify-center">
                <Users className="h-4 w-4" />
                <span className="text-xs font-medium">
                  Published experts are live on the consultations page and can receive client bookings
                </span>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
